import { useEffect } from "react";
import {
  RealtimeKitProvider,
  useRealtimeKitClient,
  useRealtimeKitMeeting,
  useRealtimeKitSelector,
} from "@cloudflare/realtimekit-react";
import { RTK_HOST_PRESET } from "./realtimeKitPresets";
import { LivestreamHostView } from "./LivestreamHostView";
import { LivestreamViewerView } from "./LivestreamViewerView";
import { LivestreamWaitingScreen } from "./LivestreamWaitingScreen";
import { useLivestreamSchedulePhase } from "./useLivestreamSchedulePhase";
import type { LivestreamMeetingExperienceProps } from "./meetingProductTypes";

function LivestreamMeetingInner({
  shopProductIds,
  leadContact,
  showLogout,
  loggingOut,
  onLogout,
  meetingStart,
  meetingEnd,
  isHostJoinLink,
  productName,
  productImageUrl,
}: Omit<LivestreamMeetingExperienceProps, "authToken">) {
  const { meeting } = useRealtimeKitMeeting();
  const roomState = useRealtimeKitSelector((m) => m.self.roomState);
  const isHost = useRealtimeKitSelector(
    (m) => m.self.presetName === RTK_HOST_PRESET,
  );
  const hasHostParticipant = useRealtimeKitSelector((m) =>
    m.participants.joined
      .toArray()
      .some((participant) => participant.presetName === RTK_HOST_PRESET),
  );
  const schedulePhase = useLivestreamSchedulePhase({
    start: meetingStart,
    end: meetingEnd,
  });

  if (!meeting || roomState !== "joined") {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-black text-white">
        Connecting...
      </div>
    );
  }

  if (isHost || isHostJoinLink) {
    return (
      <LivestreamHostView
        showLogout={showLogout}
        loggingOut={loggingOut}
        onLogout={onLogout}
      />
    );
  }

  if (schedulePhase === "ended") {
    return (
      <LivestreamWaitingScreen
        productName={productName ?? "Livestream"}
        imageUrl={productImageUrl}
        pageTitle={productName ? `${productName} · Livestream` : "Livestream"}
        start={meetingStart}
        end={meetingEnd}
        phase="ended"
      />
    );
  }

  if (schedulePhase === "waiting-for-host" && !hasHostParticipant) {
    return (
      <LivestreamWaitingScreen
        productName={productName ?? "Livestream"}
        imageUrl={productImageUrl}
        pageTitle={productName ? `${productName} · Livestream` : "Livestream"}
        start={meetingStart}
        end={meetingEnd}
        phase="waiting-for-host"
      />
    );
  }

  return (
    <LivestreamViewerView
      shopProductIds={shopProductIds}
      leadContact={leadContact}
      showLogout={showLogout}
      loggingOut={loggingOut}
      onLogout={onLogout}
    />
  );
}

function LivestreamMeetingExperienceInner(props: LivestreamMeetingExperienceProps) {
  const { authToken, ...innerProps } = props;
  const [meetingClient, initMeeting] = useRealtimeKitClient();

  useEffect(() => {
    initMeeting({
      authToken,
      defaults: { audio: false, video: false },
    });
  }, [authToken, initMeeting]);

  useEffect(() => {
    if (!meetingClient) return;
    void meetingClient.join();
  }, [meetingClient]);

  return (
    <RealtimeKitProvider value={meetingClient}>
      <LivestreamMeetingInner {...innerProps} />
    </RealtimeKitProvider>
  );
}

export function LivestreamMeetingExperience(props: LivestreamMeetingExperienceProps) {
  return <LivestreamMeetingExperienceInner {...props} />;
}
